<script setup lang="ts">
  import { nextTick, onBeforeUnmount, watch, type ComponentPublicInstance } from 'vue';
  import RuleDraftCard from './RuleDraftCard.vue';
  import RuleSavedCard from './RuleSavedCard.vue';
  import RulesEmptyState from './RulesEmptyState.vue';
  import RulesNoResults from './RulesNoResults.vue';
  import RulesSidebarToolbar from './RulesSidebarToolbar.vue';
  import { useFormBuilderRulesSidebar } from '../../composables/useFormBuilderRulesSidebar';

  const props = defineProps<{ locked?: boolean }>();

  const sidebar = useFormBuilderRulesSidebar();
  const savedRuleCardRefs = new Map<string, HTMLElement>();

  function setSavedRuleCardRef(
    ruleId: string,
    component: Element | ComponentPublicInstance | null
  ) {
    if (!component) {
      savedRuleCardRefs.delete(ruleId);
      return;
    }

    let element: HTMLElement | null = null;
    if (component instanceof HTMLElement) {
      element = component;
    } else if ('$el' in component && component.$el instanceof HTMLElement) {
      element = component.$el;
    }

    if (element) savedRuleCardRefs.set(ruleId, element);
  }

  watch(
    () =>
      [sidebar.formRulesStore.focusedRuleId, sidebar.formRulesStore.focusedRuleVersion] as const,
    async ([focusedRuleId]) => {
      if (!focusedRuleId) return;

      await nextTick();
      savedRuleCardRefs
        .get(focusedRuleId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  );

  onBeforeUnmount(() => savedRuleCardRefs.clear());

  defineExpose({
    updateDraftTriggerOption: sidebar.draft.updateTriggerOption,
    updateDraftExpanded: sidebar.draft.updateExpanded,
    handleDraftThenAction: sidebar.draft.handleThenAction,
    getDraftThenBlocks: () => sidebar.state.thenBlocks,
    syncSelectedTargetsToActiveBlock: sidebar.actions.syncSelectedTargetsToActiveBlock,
    confirmDraftThenBlockAt: sidebar.draft.confirmThenBlockAt,
    toggleAllSavedRules: sidebar.saved.toggleAll,
    toggleSavedRule: sidebar.saved.toggleRule,
    setSortDropdownOpen: sidebar.toolbar.setSortDropdownOpen,
    updateSortOption: sidebar.toolbar.updateSortOption,
  });
</script>

<template>
  <aside
    class="flex h-full w-[405px] shrink-0 flex-col border-l border-gray-02 bg-white-02"
    data-testid="form-builder-rules-sidebar"
  >
    <div class="flex items-center border-b border-gray-02 px-4 py-4">
      <h2 class="text-sm font-semibold text-gray-07">Regras</h2>
    </div>

    <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6">
      <RulesSidebarToolbar
        :search-term="sidebar.state.searchTerm"
        :actions-disabled="sidebar.toolbar.actionsDisabled"
        :export-disabled="sidebar.toolbar.exportDisabled"
        :exporting="sidebar.toolbar.isExporting"
        :toggle-label="sidebar.toolbar.toggleLabel"
        :has-expanded-rule="sidebar.toolbar.hasExpandedRule"
        :selected-sort-option="sidebar.toolbar.selectedSortOption"
        :sort-active="sidebar.toolbar.isSortActive"
        :sort-dropdown-open="sidebar.toolbar.isSortDropdownOpen"
        @update-search="sidebar.state.searchTerm = $event"
        @update-sort="sidebar.toolbar.updateSortOption"
        @update-sort-open="sidebar.toolbar.setSortDropdownOpen"
        @toggle-all="sidebar.saved.toggleAll"
        @export-rules-list="sidebar.toolbar.exportReportFields"
      />

      <div
        v-if="sidebar.rules.filtered.length > 0"
        class="flex flex-col gap-5"
        data-testid="rules-list"
      >
        <RuleSavedCard
          v-for="rule in sidebar.rules.filtered"
          :key="`${rule.id}-${sidebar.rules.renderVersion}`"
          :ref="(component) => setSavedRuleCardRef(rule.id, component)"
          :rule="rule"
          :expanded="
            sidebar.formRulesStore.ruleItemSelectionActive &&
            sidebar.state.activeSavedThenBlock?.ruleId !== rule.id
              ? false
              : sidebar.saved.isExpanded(rule.id)
          "
          :trigger-options="sidebar.saved.getTriggerSelectOptions(rule)"
          :action-options="sidebar.options.action"
          :active-then-block-id="
            sidebar.state.activeSavedThenBlock?.ruleId === rule.id
              ? sidebar.state.activeSavedThenBlock.blockId
              : null
          "
          :editing-locked="sidebar.rules.isSavedEditingLocked"
          :readonly="props.locked"
          :name-error="rule.nameError"
          :unsaved-error="rule.unsavedError"
          :reset-version="sidebar.rules.renderVersion"
          :is-focused="sidebar.formRulesStore.focusedRuleId === rule.id"
          :focus-version="sidebar.formRulesStore.focusedRuleVersion"
          @toggle="sidebar.saved.toggleRule"
          @update-rule-name="sidebar.saved.updateRuleName(rule, $event)"
          @update-trigger-option="sidebar.saved.updateTriggerOption(rule, $event)"
          @update-then-action="sidebar.saved.handleThenAction(rule, $event)"
          @add-item="sidebar.saved.addThenItems(rule, $event)"
          @delete-then="sidebar.saved.handleThenDelete(rule, $event)"
          @confirm-then="sidebar.saved.confirmThenItems"
          @remove-target="sidebar.saved.handleTargetRemoval(rule, $event)"
          @add-condition="sidebar.saved.addThenBlock(rule)"
          @delete-rule="sidebar.saved.removeRule(rule.id)"
        />
      </div>

      <RulesNoResults v-if="sidebar.rules.noResultsFound" data-testid="rules-no-results" />

      <div
        v-if="sidebar.rules.pendingCreationDrafts.length > 0"
        class="flex flex-col gap-5"
        data-testid="pending-rules-list"
      >
        <div
          v-for="(draft, draftIndex) in sidebar.rules.pendingCreationDrafts"
          :key="`pending-draft-${draft.triggerFieldId}-${draftIndex}`"
          class="flex shrink-0 flex-col"
        >
          <RuleDraftCard
            :rule-name="draft.ruleName"
            :expanded="sidebar.formRulesStore.ruleItemSelectionActive ? false : draft.isExpanded"
            :trigger-option="draft.triggerOption"
            :trigger-options="draft.triggerOptions"
            :then-blocks="draft.thenBlocks"
            :action-options="sidebar.options.action"
            :active-then-block-id="null"
            :has-unsaved-error="sidebar.formRulesStore.ruleCreationDraftUnsavedError"
            :readonly="props.locked || sidebar.formRulesStore.ruleItemSelectionActive"
            @update-rule-name="sidebar.pending.updateRuleName(draftIndex, $event)"
            @update-expanded="sidebar.pending.updateExpanded(draftIndex, $event)"
            @update-trigger-option="sidebar.pending.updateTriggerOption(draftIndex, $event)"
            @update-then-action="sidebar.pending.handleThenAction(draftIndex, $event)"
            @add-item="sidebar.pending.addThenItems(draftIndex, $event)"
            @delete-then="sidebar.pending.handleThenDelete(draftIndex, $event)"
            @remove-target="sidebar.pending.handleTargetRemoval(draftIndex, $event)"
            @add-condition="sidebar.pending.addThenBlock(draftIndex)"
            @delete-rule="sidebar.pending.removeRule(draftIndex)"
          />
          <p
            v-if="sidebar.formRulesStore.ruleCreationDraftUnsavedError"
            class="mt-0.5 text-xs leading-[18px] text-[var(--theme-secondary-ink)]"
            data-testid="pending-rule-unsaved-error"
          >
            Regra não salva
          </p>
        </div>
      </div>

      <div
        v-if="sidebar.rules.isCreating"
        :ref="sidebar.draft.setFormRef"
        class="flex shrink-0 flex-col"
      >
        <RuleDraftCard
          :key="`draft-${sidebar.formRulesStore.draftTriggerFieldId ?? 'none'}-${sidebar.formRulesStore.ruleCreationSessionId}`"
          :rule-name="sidebar.state.ruleName"
          :expanded="sidebar.state.isRuleExpanded"
          :trigger-option="sidebar.state.selectedTriggerOption"
          :trigger-options="sidebar.options.trigger"
          :then-blocks="sidebar.state.thenBlocks"
          :action-options="sidebar.options.action"
          :active-then-block-id="sidebar.state.activeThenBlockId"
          :name-disabled="sidebar.formRulesStore.ruleItemSelectionActive"
          :readonly="
            props.locked ||
            (sidebar.formRulesStore.ruleItemSelectionActive && !sidebar.state.activeThenBlockId)
          "
          :has-unsaved-error="sidebar.formRulesStore.ruleCreationDraftUnsavedError"
          :add-disabled="
            sidebar.rules.isSavedEditingLocked ||
            !sidebar.state.selectedTriggerOption ||
            !sidebar.state.thenBlocks[sidebar.state.thenBlocks.length - 1]?.confirmed ||
            sidebar.state.thenBlocks.length >= 3
          "
          @update-rule-name="sidebar.draft.updateRuleName"
          @update-expanded="sidebar.draft.updateExpanded"
          @update-trigger-option="sidebar.draft.updateTriggerOption"
          @update-then-action="sidebar.draft.handleThenAction"
          @add-item="sidebar.draft.addThenItems"
          @delete-then="sidebar.draft.handleThenDelete"
          @confirm-then="sidebar.draft.confirmThenItems"
          @remove-target="sidebar.draft.handleTargetRemoval"
          @add-condition="sidebar.draft.addThenBlock"
          @delete-rule="sidebar.draft.removeRule"
        />
        <p
          v-if="sidebar.formRulesStore.ruleCreationDraftUnsavedError"
          class="mt-0.5 text-xs leading-[18px] text-[var(--theme-secondary-ink)]"
          data-testid="rule-unsaved-error"
        >
          Regra não salva
        </p>
      </div>

      <RulesEmptyState
        v-if="
          !sidebar.state.searchTerm.trim() &&
          !sidebar.rules.isCreating &&
          sidebar.formRulesStore.rules.length === 0 &&
          sidebar.rules.pendingCreationDrafts.length === 0
        "
      />
    </div>
  </aside>
</template>
